import { useEffect } from 'react'
import { unlockNativeAudio } from '../services/audio-manager'
import { getNativePlatform, isNativeApp } from '../utils/platform'

/** Match the app surface — adaptive status bar (white bar, dark icons). */
const STATUS_BAR_BG = '#ffffff'

/** Configure Capacitor status bar, keyboard resize, and native layout classes on mount. */
export function useNativeShell() {
  useEffect(() => {
    if (!isNativeApp()) return

    document.documentElement.classList.add('native-app')
    const platformClass = `native-${getNativePlatform()}`
    document.documentElement.classList.add(platformClass)
    unlockNativeAudio()

    let cancelled = false
    const keyboardListenerRemoves: Array<() => Promise<void>> = []
    let detachViewport: (() => void) | undefined
    let detachStatusBarObserver: (() => void) | undefined
    let detachStatusBarInset: (() => void) | undefined

    void (async () => {
      try {
        const { StatusBar, Style } = await import('@capacitor/status-bar')
        const attempt = async (step: () => Promise<unknown>) => {
          try {
            await step()
          } catch {
            /* one unsupported call must not skip the rest */
          }
        }
        // Light app surface → dark icons. Capacitor semantics: Style.Light =
        // "light background, dark text/icons". (capacitor.config.ts sets the same
        // default so the plugin's per-viewDidAppear reset restores it.)
        await attempt(() => StatusBar.setStyle({ style: Style.Light }))
        if (getNativePlatform() === 'ios') {
          // iOS stays EDGE-TO-EDGE: the WebView spans the whole screen and every
          // header/bar pads itself with --safe-top / --safe-bottom (index.css).
          // Do NOT switch the plugin to overlay:false — it only moves the WebView
          // down until the next bridge viewDidAppear (e.g. after the photo picker
          // or a system sheet closes) silently restores overlay:true, which put
          // the status bar on top of the thread header and its Call/Video buttons.
          await attempt(() => StatusBar.setOverlaysWebView({ overlay: true }))
          await attempt(() => StatusBar.show())
          // Fallback for --safe-top when the CSS safe-area inset reports 0:
          // the plugin knows the real status-bar height (0 when hidden, e.g.
          // landscape iPhone). Re-read on rotation.
          const publishInset = async () => {
            try {
              const info = await StatusBar.getInfo()
              const height = info.visible ? Math.max(0, Math.round((info as { height?: number }).height ?? 0)) : 0
              document.documentElement.style.setProperty('--native-inset-top', `${height}px`)
            } catch {
              /* env() alone then */
            }
          }
          await publishInset()
          window.addEventListener('resize', publishInset)
          const stopInsetUpdates = () => window.removeEventListener('resize', publishInset)
          detachStatusBarInset = stopInsetUpdates
          if (cancelled) stopInsetUpdates()
        } else {
          await attempt(() => StatusBar.setOverlaysWebView({ overlay: false }))
          await attempt(() => StatusBar.show())
          // Ignored on Android 15+ (edge-to-edge); still applies on older devices.
          await attempt(() => StatusBar.setBackgroundColor({ color: STATUS_BAR_BG }))
        }
      } catch {
        /* non-fatal on web or unsupported platforms */
      }

      if (getNativePlatform() === 'ios') {
        // Dark full-screen surfaces (calls, image viewer) mark their root with
        // data-dark-surface="true"; flip the status bar to light icons while one
        // is on screen and back when it closes.
        try {
          const { StatusBar, Style } = await import('@capacitor/status-bar')
          const MARKER = '[data-dark-surface="true"]'
          let lastDark = false
          let frame = 0
          const apply = () =>
            void StatusBar.setStyle({ style: lastDark ? Style.Dark : Style.Light }).catch(() => {})
          const sync = () => {
            frame = 0
            const dark = document.querySelector(MARKER) !== null
            if (dark === lastDark) return
            lastDark = dark
            apply()
          }
          const touchesMarker = (node: Node) =>
            node instanceof Element && (node.matches(MARKER) || node.querySelector(MARKER) !== null)
          const root = document.getElementById('root') ?? document.body
          // Overlays render inside #root (no portals). Only react to the marker
          // attribute changing or to added/removed subtrees that carry the marker —
          // ordinary list churn (messages, presence) is filtered out here.
          const observer = new MutationObserver((records) => {
            const relevant = records.some(
              (r) =>
                r.type === 'attributes' ||
                Array.from(r.addedNodes).some(touchesMarker) ||
                Array.from(r.removedNodes).some(touchesMarker),
            )
            if (relevant && !frame) frame = requestAnimationFrame(sync)
          })
          observer.observe(root, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['data-dark-surface'],
          })
          // Pick up a surface that was already on screen when we attached.
          sync()
          // The plugin re-applies capacitor.config.ts values (style LIGHT) on every
          // bridge viewDidAppear; re-assert the style for the current surface when
          // the app returns to the foreground.
          const onResume = () => {
            if (document.visibilityState === 'visible') apply()
          }
          document.addEventListener('visibilitychange', onResume)
          detachStatusBarObserver = () => {
            observer.disconnect()
            document.removeEventListener('visibilitychange', onResume)
            if (frame) cancelAnimationFrame(frame)
          }
          if (cancelled) detachStatusBarObserver()
        } catch {
          /* plugin optional */
        }
      }

      try {
        const { Keyboard, KeyboardResize } = await import('@capacitor/keyboard')
        // iOS-only API (no-op on Android). Android resizing is handled by
        // resizeOnFullScreen in capacitor.config.ts + adjustResize.
        try {
          await Keyboard.setResizeMode({ mode: KeyboardResize.Native })
        } catch {
          /* unimplemented on Android */
        }
        const snapScrollToTop = () => {
          window.scrollTo(0, 0)
          document.documentElement.scrollTop = 0
          document.body.scrollTop = 0
        }

        let keyboardOpen = false

        const track = async (promise: Promise<{ remove: () => Promise<void> }>) => {
          const handle = await promise
          if (cancelled) {
            void handle.remove()
            return
          }
          keyboardListenerRemoves.push(() => handle.remove())
        }

        // Safety net: if the WebView resize lands after the browser already
        // scrolled the document to reveal the focused input, snap back so the
        // chat header stays pinned at the top.
        await track(Keyboard.addListener('keyboardDidShow', snapScrollToTop))
        await track(
          Keyboard.addListener('keyboardDidHide', () => {
            snapScrollToTop()
            // resizeOnFullScreen can leave a stale inset when the keyboard hides
            // (visible as a gap above the composer). Nudge layout on the next
            // frame so the WebView re-measures the viewport.
            requestAnimationFrame(() => {
              snapScrollToTop()
              window.dispatchEvent(new Event('resize'))
            })
          }),
        )

        // iOS can offset the visual viewport when the keyboard opens (especially
        // if a sub-16px input triggered auto-zoom). Pin layout to the top.
        await track(
          Keyboard.addListener('keyboardWillShow', () => {
            keyboardOpen = true
            snapScrollToTop()
          }),
        )
        await track(
          Keyboard.addListener('keyboardWillHide', () => {
            keyboardOpen = false
          }),
        )

        const vv = window.visualViewport
        if (vv) {
          const onViewportShift = () => {
            if (!keyboardOpen) return
            if (window.scrollY !== 0 || vv.offsetTop !== 0) {
              snapScrollToTop()
            }
          }
          vv.addEventListener('scroll', onViewportShift)
          vv.addEventListener('resize', onViewportShift)
          detachViewport = () => {
            vv.removeEventListener('scroll', onViewportShift)
            vv.removeEventListener('resize', onViewportShift)
          }
        }

        // Android: the WebView sometimes ignores an input focus and never
        // summons the soft keyboard (observed after lock-screen call flows).
        // Explicitly request it whenever a text-entry element gains focus —
        // Keyboard.show() is Android-only and a no-op when already visible.
        if (getNativePlatform() === 'android') {
          const isTextEntry = (el: EventTarget | null): boolean => {
            if (el instanceof HTMLTextAreaElement) return true
            if (el instanceof HTMLElement && el.isContentEditable) return true
            if (el instanceof HTMLInputElement) {
              return ![
                'button', 'checkbox', 'radio', 'range', 'file',
                'submit', 'reset', 'color', 'hidden',
              ].includes(el.type)
            }
            return false
          }
          const onFocusIn = (e: FocusEvent) => {
            if (!isTextEntry(e.target)) return
            void Keyboard.show().catch(() => {
              /* best effort */
            })
          }
          document.addEventListener('focusin', onFocusIn)
          const prevDetach = detachViewport
          detachViewport = () => {
            prevDetach?.()
            document.removeEventListener('focusin', onFocusIn)
          }
        }
      } catch {
        /* plugin optional until cap sync */
      }
    })()

    return () => {
      cancelled = true
      void Promise.all(keyboardListenerRemoves.map((remove) => remove()))
      detachViewport?.()
      detachStatusBarObserver?.()
      detachStatusBarInset?.()
      document.documentElement.style.removeProperty('--native-inset-top')
      document.documentElement.classList.remove('native-app')
      document.documentElement.classList.remove(platformClass)
    }
  }, [])
}
